// Libs
import { DateTime } from 'luxon'
import { omitBy, isNil } from 'lodash'

/**
 * Import item properties
 * @typedef SubsReportFilterForm
 * @type {Object}
 * @property {Array<String>} [docs] - List of doc number
 * @property {String} [dateType]    - Period type
 * @property {String} [dateStart]   - Period start
 * @property {String} [dateStop]    - Period stop
 * @property {String} [status]      - Subcription status
 * @property {String} [pkg]         - Package type
 * @property {String} [receipt]     - Receipt type
 * @property {String} [seller]      - Seller id
 * @property {String} [groupName]   - Group name
 * @property {Number} [projectId]   - Project ID
 * @property {Boolean} [usedCoin]   - Used MNC flag
 */

/**
 * Fetch list pager, kept separate from filter criteria
 * @typedef SubsReportPager
 * @type {Object}
 * @property {Number} page  - Fetch page
 * @property {Number} limit - Fetch limitation
 */

export class SubsReportFilterCommon {

  // Max date range (days)
  static maxRangeDays = 93

  // Start of today
  static startOfToday() {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }

  // End of today
  static endOfToday() {
    const d = new Date()
    d.setHours(23, 59, 59, 999)
    return d
  }

  // Start
  static dateMax(time, stop) {

    // Disable future date
    if (time.getTime() > this.endOfToday().getTime()) return true

    // Validate stop date
    if (!stop) return false

    // Start cannot be after stop
    return (
      time.getTime() > new Date(stop).getTime()
    )
  }

  // Stop
  static dateMin(time, start) {

    // Disable future date
    if (time.getTime() > this.endOfToday().getTime()) return true

    // Validate start date
    if (!start) return false

    // Calculate Min/Max (3 months range)
    const min = new Date(start).getTime()
    const max = new Date(start).getTime() + (this.maxRangeDays * 24 * 60 * 60 * 1000)

    return (
      time.getTime() < min || time.getTime() > max
    )
  }

  /** @type {SubsReportFilterForm} */
  static filter = () => ({
    docs      : [],
    dateType  : null,
    dateStart : this.startOfToday(),
    dateStop  : this.endOfToday(),
    status    : null,
    pkg       : null,
    receipt   : null,
    seller    : null,
    groupName : null,
    projectId : null,
    usedCoin  : false,
  })

  /** @type {SubsReportPager} */
  static pager = (limit = 30) => ({
    page : 1, limit
  })

  // Remove filter field
  static removeField = (form, key) => {

    // Cleared form
    const next = { ...form }

    switch (key) {

      // Date type
      case 'dateType':
        next.dateType = null
      break

      // Date range
      case 'date':
        next.dateStart = null
        next.dateStop = null
      break

      // Group name
      case 'groupName':
        next.groupName = null
      break

      // Package
      case 'pkg':
        next.pkg = null
      break

      // Status
      case 'status':
        next.status = null
      break

      // Seller
      case 'seller':
        next.seller = null
      break

      // Receipt
      case 'receipt':
        next.receipt = null
      break

      // Project
      case 'projectId':
        next.projectId = null
      break

      // Used coin
      case 'usedCoin':
        next.usedCoin = false
      break

      // Docs
      case 'docs':
        next.docs = []
      break

      default:
        break
    }

    return next
  }

  // Reset all fields
  static resetForm = (form) => (
    Object.assign(this.filter(), {
      projectId: form.projectId
    })
  )

  // Query value parsers
  static queryParsers = {
    projectId : (value) => parseInt(value, 10),
    docs      : (value) => [].concat(value),
    dateStart : (value) => new Date(value),
    dateStop  : (value) => new Date(value),
    usedCoin  : (value) => value === 'true',
  }

  // Format date to ISO
  static toISO = value => (
    value ? DateTime.fromJSDate(new Date(value)).toISODate() : undefined
  )

  // Form value serializers
  static querySerializers = {
    docs      : (value) => value && value.length ? value : undefined,
    dateStart : (value) => this.toISO(value),
    dateStop  : (value) => this.toISO(value),
    usedCoin  : (value) => value ? 'true' : undefined,
  }

  // Parse route query into filter form
  static fromQuery = (query = {}) => {

    // Base filter
    const base = this.filter()

    // Base pager
    const basePager = this.pager()

    // Reduce query into filter form
    const reducer = (form, key) => {

      // Query value
      const value = query[key]

      // Value parser
      const parse = this.queryParsers[key]

      // Resolved value
      let resolved = value
      if (value === undefined) {
        resolved = base[key]
      } else if (parse) {
        resolved = parse(value)
      }

      // Set form
      return {
        ...form,
        [key]: resolved
      }
    }

    // Parse page
    const page = (
      query.page ? parseInt(query.page, 10) : basePager.page
    )

    // Parse limit
    const limit = (
      query.limit ? parseInt(query.limit, 10) : basePager.limit
    )

    // Set filter
    const params = (
      Object.keys(base).reduce(reducer, {})
    )

    return Object.assign(
      params, { page, limit }
    )
  }

  // Serialize filter form into route query
  static toQuery = (form, pager = this.pager()) => {

    // Base filter
    const base = this.filter()

    // Reduce form into route query
    const reducer = (query, key) => {

      // Form value
      const value = form[key]

      // Value serializer
      const serialize = this.querySerializers[key]

      // Set query
      return {
        ...query,
        [key]: serialize ? serialize(value) : (value || undefined)
      }
    }

    // Pager props
    const { page, limit } = pager

    // Set filter
    const params = Object.assign(
      Object.keys(base).reduce(reducer, {}), { page, limit }
    )

    return omitBy(params, isNil)
  }
}